import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { UpgradeModal } from '../components/UpgradeModal';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradieDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateService, setShowCreateService] = useState(false);
  const [categories, setCategories] = useState([]);
  const [freeNow, setFreeNow] = useState(false);
  const [freeDuration, setFreeDuration] = useState(2);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  
  const [newService, setNewService] = useState({
    title: '',
    short_description: '',
    detailed_description: '',
    price_base: '',
    price_mode: 'gst_included',
    category_id: '',
    subcategory_id: '',
    suburb: '',
    postcode: '',
    state: 'WA',
    inclusions: ['Free quote', 'Quality materials', 'Clean up after work'],
    exclusions: ['Materials (quoted separately)', 'Permit fees'],
    media_urls: [],
  });
  const [subcategories, setSubcategories] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (profile) {
      setFreeNow(profile.free_now || false);
    }
  }, [profile]);

  useEffect(() => {
    if (newService.category_id) {
      fetchSubcategories(newService.category_id);
    }
  }, [newService.category_id]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setProfile(response.data.tradie_profile);
      
      // Fetch tradie's services
      const servicesResponse = await axios.get(`${API}/tradies/me/services`);
      setServices(servicesResponse.data.services || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/categories/${categoryId}/subcategories`);
      setSubcategories(response.data.subcategories || []);
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
    }
  };

  const handleMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const tierLimits = {
      basic: 3,
      premium: 8,
      pro: 12
    };
    const limit = tierLimits[profile?.current_tier || 'basic'];

    if (newService.media_urls.length >= limit) {
      setUpgradeReason(`Photo limit reached (${limit}). Upgrade to add more photos per listing.`);
      setShowUpgradeModal(true);
      return;
    }

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewService({
        ...newService,
        media_urls: [...newService.media_urls, response.data.url]
      });
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingMedia(false);
    }
  };

  const removeMedia = (index) => {
    setNewService({
      ...newService,
      media_urls: newService.media_urls.filter((_, i) => i !== index)
    });
  };

  const getTierBadge = (tier) => {
    const badges = {
      basic: <Badge variant="secondary" className="bg-gray-500 text-white">Basic</Badge>,
      premium: <Badge className="bg-blue-500 text-white">Premium</Badge>,
      pro: <Badge className="bg-yellow-500 text-white">Pro</Badge>,
    };
    return badges[tier] || badges.basic;
  };

  const toggleFreeNow = async () => {
    try {
      await axios.patch(`${API}/tradies/me/availability`, null, {
        params: {
          free_now: !freeNow,
          duration_hours: freeDuration
        }
      });
      setFreeNow(!freeNow);
      toast.success(freeNow ? 'Availability turned off' : `You're now marked as available for ${freeDuration}h`);
      fetchProfile();
    } catch (error) {
      console.error('Failed to update availability:', error);
      toast.error('Failed to update availability');
    }
  };

  const getVerificationBadge = (status) => {
    const badges = {
      verified: <Badge className="bg-green-500 text-white">✓ Verified</Badge>,
      pending: <Badge variant="outline" className="text-yellow-600">⏳ Pending</Badge>,
      rejected: <Badge variant="destructive">✗ Rejected</Badge>,
      not_provided: <Badge variant="outline">Not Provided</Badge>,
      not_required: <Badge variant="secondary">Not Required</Badge>,
    };
    return badges[status] || badges.not_provided;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Profile not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="tradie-dashboard">
      {/* Email Verification Banner */}
      <EmailVerificationBanner user={user} onVerified={fetchProfile} />
      
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Dashboard</h1>
          <p className="text-muted-foreground">Work starts here—manage your services and profile</p>
        </div>
        {getTierBadge(profile.current_tier)}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground mb-1">Active Listings</div>
            <div className="text-3xl font-bold">{profile.listings_count} / {profile.listings_limit}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground mb-1">Profile Views</div>
            <div className="text-3xl font-bold">-</div>
            <div className="text-xs text-muted-foreground mt-1">Coming soon</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-muted-foreground mb-1">Messages</div>
            <div className="text-3xl font-bold">-</div>
            <div className="text-xs text-muted-foreground mt-1">Coming soon</div>
          </CardContent>
        </Card>
        
        {/* I'm Free Now Card */}
        <Card className={freeNow ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : ''}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">I'm Free Now</div>
              {freeNow && <Badge className="bg-green-500 text-white">🟢 Active</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Select value={freeDuration.toString()} onValueChange={(v) => setFreeDuration(parseInt(v))} disabled={freeNow}>
                <SelectTrigger className="h-8 text-xs w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1h</SelectItem>
                  <SelectItem value="2">2h</SelectItem>
                  <SelectItem value="3">3h</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={toggleFreeNow}
                className={freeNow ? 'bg-destructive hover:bg-destructive/90' : 'bg-green-500 hover:bg-green-600'}
                data-testid="free-now-toggle"
              >
                {freeNow ? 'Turn Off' : 'Turn On'}
              </Button>
            </div>
            {freeNow && profile.free_now_until && (
              <p className="text-xs text-muted-foreground">
                Until {new Date(profile.free_now_until).toLocaleTimeString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="verification">Verification</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Your Services</h2>
            <Button
              onClick={() => {
                if (!user?.email_verified) {
                  toast.error('Please verify your email before creating services');
                  return;
                }
                if (profile.listings_count >= profile.listings_limit) {
                  setUpgradeReason(`You've reached your listing limit (${profile.listings_limit}). Upgrade to add more services.`);
                  setShowUpgradeModal(true);
                } else {
                  setShowCreateService(true);
                }
              }}
              data-testid="create-service-button"
            >
              + Add Service
            </Button>
          </div>

          {services.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center space-y-4">
                <div className="text-6xl">📝</div>
                <h3 className="text-xl font-semibold">Work starts here</h3>
                <p className="text-muted-foreground">Create your first service listing to start attracting customers</p>
                <Button onClick={() => setShowCreateService(true)}>Create Service</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {services.map((service) => (
                <Card key={service.id} data-testid={`my-service-${service.id}`}>
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {/* Thumbnail */}
                      <div className="w-32 h-24 bg-gradient-to-br from-primary/10 to-accent-green/10 rounded-xl flex-shrink-0" />
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">{service.title}</h3>
                            <p className="text-sm text-muted-foreground">{service.suburb}, {service.state}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-500">★</span>
                            <span className="text-sm">{service.rating_avg.toFixed(1)}</span>
                            <span className="text-sm text-muted-foreground">({service.rating_count})</span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2">{service.short_description}</p>
                        
                        <div className="flex items-center justify-between pt-2">
                          <div className="space-x-2">
                            <span className="text-lg font-bold">${service.price_base.toFixed(2)}</span>
                            {service.price_mode === 'plus_gst' && <Badge variant="outline" className="text-xs">+GST</Badge>}
                            {service.price_mode === 'gst_included' && <Badge variant="outline" className="text-xs">GST inc</Badge>}
                            {service.price_mode === 'no_gst' && <Badge variant="outline" className="text-xs">No GST</Badge>}
                          </div>
                          
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/services/${service.id}`)}>
                              View
                            </Button>
                            <Button size="sm" variant="outline">
                              Edit
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>Manage your business profile</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Business Name</Label>
                <p className="text-lg font-medium">{profile.business_name}</p>
              </div>
              
              <div>
                <Label>Business Type</Label>
                <p className="capitalize">{profile.tradie_type}</p>
              </div>
              
              <div>
                <Label>Years Experience</Label>
                <p>{profile.years_experience || 'Not set'}</p>
              </div>
              
              <div>
                <Label>GST Registered</Label>
                <p>{profile.gst_registered ? 'Yes' : 'No'}</p>
              </div>

              <Button variant="outline">Edit Profile</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Review Management</CardTitle>
              <CardDescription>
                Manage your reviews 
                {profile.current_tier === 'premium' && ' • Premium: Reply to reviews, pin 1'}
                {profile.current_tier === 'pro' && ' • Pro: Reply to reviews, pin 3'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Review management interface will load your reviews here</p>
                {profile.current_tier === 'basic' && (
                  <div className="mt-4">
                    <Badge variant="outline" className="mb-2">Upgrade to Premium or Pro</Badge>
                    <p className="text-sm">Reply to reviews and pin your best ones</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verification" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Verification Status</CardTitle>
              <CardDescription>Build trust with verified credentials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <Label>ABN</Label>
                    <p className="text-sm text-muted-foreground">{profile.abn || 'Not provided'}</p>
                  </div>
                  {getVerificationBadge(profile.abn_status)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <Label>Licence</Label>
                    <p className="text-sm text-muted-foreground">{profile.licence_number || 'Not provided'}</p>
                  </div>
                  {getVerificationBadge(profile.licence_status)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <Label>Insurance</Label>
                    <p className="text-sm text-muted-foreground">{profile.insurance_details || 'Not provided'}</p>
                  </div>
                  {getVerificationBadge(profile.insurance_status)}
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button variant="outline">Update Credentials</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="gf-grad-accent border-0">
            <CardContent className="p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">🔒 Why verify?</h3>
              <ul className="space-y-1 text-sm text-white/90">
                <li>• Build trust with customers</li>
                <li>• Stand out from competitors</li>
                <li>• Get priority in search results</li>
                <li>• Access premium features</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Service Dialog */}
      <Dialog open={showCreateService} onOpenChange={setShowCreateService}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Service</DialogTitle>
            <DialogDescription>
              Add a new service to your listings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Service Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Professional Plumbing Services"
                value={newService.title}
                onChange={(e) => setNewService({ ...newService, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="short_description">Short Description * (Max 200 chars)</Label>
              <Input
                id="short_description"
                placeholder="Brief description..."
                maxLength={200}
                value={newService.short_description}
                onChange={(e) => setNewService({ ...newService, short_description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="detailed_description">Detailed Description *</Label>
              <Textarea
                id="detailed_description"
                placeholder="Detailed description of your service..."
                rows={4}
                value={newService.detailed_description}
                onChange={(e) => setNewService({ ...newService, detailed_description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={newService.category_id} onValueChange={(value) => setNewService({ ...newService, category_id: value, subcategory_id: '' })}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory {newService.category_id && '*'}</Label>
                <Select 
                  value={newService.subcategory_id} 
                  onValueChange={(value) => setNewService({ ...newService, subcategory_id: value })}
                  disabled={!newService.category_id}
                >
                  <SelectTrigger id="subcategory">
                    <SelectValue placeholder={newService.category_id ? "Select subcategory" : "Select category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map((subcat) => (
                      <SelectItem key={subcat.id} value={subcat.id}>{subcat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="suburb">Suburb *</Label>
                <Input
                  id="suburb"
                  placeholder="Morley"
                  value={newService.suburb}
                  onChange={(e) => setNewService({ ...newService, suburb: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode *</Label>
                <Input
                  id="postcode"
                  placeholder="6062"
                  value={newService.postcode}
                  onChange={(e) => setNewService({ ...newService, postcode: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value="WA"
                  disabled
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price_base">Starting Price (AUD) *</Label>
                <Input
                  id="price_base"
                  type="number"
                  step="0.01"
                  placeholder="120.00"
                  value={newService.price_base}
                  onChange={(e) => setNewService({ ...newService, price_base: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_mode">GST *</Label>
              <Select value={newService.price_mode} onValueChange={(value) => setNewService({ ...newService, price_mode: value })}>
                <SelectTrigger id="price_mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gst_included">GST Included</SelectItem>
                  <SelectItem value="plus_gst">Plus GST</SelectItem>
                  <SelectItem value="no_gst">No GST</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Media Upload */}
            <div className="space-y-3">
              <Label>Photos ({newService.media_urls.length}/{profile?.current_tier === 'basic' ? 3 : profile?.current_tier === 'premium' ? 8 : 12})</Label>
              
              <div className="grid grid-cols-4 gap-3">
                {newService.media_urls.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-muted group">
                    <img src={url} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeMedia(index)}
                      className="absolute top-1 right-1 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                
                {newService.media_urls.length < (profile?.current_tier === 'basic' ? 3 : profile?.current_tier === 'premium' ? 8 : 12) && (
                  <label className="aspect-square rounded-xl border-2 border-dashed border-muted-foreground/30 hover:border-primary transition-colors cursor-pointer flex flex-col items-center justify-center gap-2">
                    <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs text-muted-foreground">Add Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleMediaUpload}
                      disabled={uploadingMedia}
                    />
                  </label>
                )}
              </div>
              
              {uploadingMedia && (
                <p className="text-sm text-muted-foreground">Uploading...</p>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateService(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  // Validate form
                  if (!newService.title || !newService.short_description || !newService.detailed_description || !newService.price_base || !newService.category_id || !newService.suburb || !newService.postcode) {
                    toast.error('Please fill all required fields');
                    return;
                  }
                  
                  try {
                    await axios.post(`${API}/services`, {
                      title: newService.title,
                      short_description: newService.short_description,
                      detailed_description: newService.detailed_description,
                      price_base: parseFloat(newService.price_base),
                      price_mode: newService.price_mode,
                      category_id: newService.category_id,
                      subcategory_id: newService.subcategory_id || newService.category_id,
                      suburb: newService.suburb,
                      postcode: newService.postcode,
                      state: newService.state,
                      inclusions: newService.inclusions,
                      exclusions: newService.exclusions,
                      media_urls: newService.media_urls
                    });
                    
                    toast.success('Service created successfully!');
                    setShowCreateService(false);
                    setNewService({
                      title: '',
                      short_description: '',
                      detailed_description: '',
                      price_base: '',
                      price_mode: 'gst_included',
                      category_id: '',
                      subcategory_id: '',
                      suburb: '',
                      postcode: '',
                      state: 'WA',
                      inclusions: ['Free quote', 'Quality materials', 'Clean up after work'],
                      exclusions: ['Materials (quoted separately)', 'Permit fees'],
                    });
                    fetchProfile();
                  } catch (error) {
                    console.error('Failed to create service:', error);
                    toast.error(error.response?.data?.detail || 'Failed to create service');
                  }
                }}
                className="flex-1"
              >
                Create Service
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={profile?.current_tier}
        reason={upgradeReason}
      />
    </div>
  );
};

export default TradieDashboard;
